import * as React from 'react';
import { EMPTY_REP_CARD_FILTERS, type RepCardFilters } from './repCardFilters';

const FILTERS_STORAGE_KEY = 'ai-coaching-review:rep-card-filters';
const FILTERS_OPEN_STORAGE_KEY = 'ai-coaching-review:rep-card-filters-open';

const readMap = (storageKey: string): Record<string, unknown> => {
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (!raw) {
			return {};
		}
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
	} catch {
		return {};
	}
};

/**
 * One rep card's slice of a per-rep `localStorage` map, so each card keeps its own state across
 * remounts (opening a call replaces the grid) and reloads.
 *
 * Writes re-read the stored map before merging: every card shares one storage key, so merging into a
 * snapshot captured at mount would clobber sibling cards' entries.
 */
function usePersistedRepCardEntry<T>(storageKey: string, repId: string, hydrate: (stored: unknown) => T) {
	const [value, setValueState] = React.useState<T>(() => hydrate(readMap(storageKey)[repId]));

	const setValue = React.useCallback(
		(next: T) => {
			setValueState(next);
			try {
				window.localStorage.setItem(storageKey, JSON.stringify({ ...readMap(storageKey), [repId]: next }));
			} catch {
				// Ignore write failures (private mode / storage disabled) — in-memory state still updates.
			}
		},
		[storageKey, repId]
	);

	return [value, setValue] as const;
}

export function usePersistedRepCardFilters(repId: string) {
	return usePersistedRepCardEntry<RepCardFilters>(FILTERS_STORAGE_KEY, repId, stored =>
		stored && typeof stored === 'object' && !Array.isArray(stored)
			? { ...EMPTY_REP_CARD_FILTERS, ...(stored as Partial<RepCardFilters>) }
			: EMPTY_REP_CARD_FILTERS
	);
}

/** Collapsed until asked for: three rows of filter chrome per card is most of a small card. */
export function usePersistedRepCardFiltersOpen(repId: string) {
	return usePersistedRepCardEntry<boolean>(FILTERS_OPEN_STORAGE_KEY, repId, stored => stored === true);
}
